import { getAxiosInstance } from '@/shared/api/axiosInstance';
import { unwrapAxiosData } from '@/shared/api/apiResponse';
import { PaddleCheckoutPlanKey } from '@/entities/billing/lib/paddlePlans';

const axiosInstance = getAxiosInstance('builder-creator');

export const createPaddleCheckout = async (
  plan: PaddleCheckoutPlanKey,
  consent?: PaddleRefundConsent
): Promise<{ transactionId: string }> => {
  const res = await axiosInstance.post('/billing/paddle/checkout', {
    plan,
    ...consent,
  });
  return unwrapAxiosData<{ transactionId: string }>(res);
};

export const getSalesAgentPaddleConfig = async (): Promise<{
  enabled: boolean;
}> => {
  const res = await axiosInstance.get('/billing/paddle/sales-agent/config');
  return unwrapAxiosData<{ enabled: boolean }>(res);
};

export const createSalesAgentPaddleCheckout = async (
  consent: PaddleRefundConsent
): Promise<{
  transactionId: string;
}> => {
  const res = await axiosInstance.post(
    '/billing/paddle/sales-agent/checkout',
    consent
  );
  return unwrapAxiosData<{ transactionId: string }>(res);
};

export type SalesAgentPaddleSubscription = {
  status: string;
  currentPeriodEnd?: string;
  cancellationScheduled: boolean;
  cancellationEffectiveAt?: string;
};

export const getSalesAgentPaddleSubscription =
  async (): Promise<SalesAgentPaddleSubscription | null> => {
    const res = await axiosInstance.get(
      '/billing/paddle/sales-agent/subscription'
    );
    return unwrapAxiosData<SalesAgentPaddleSubscription | null>(res);
  };

export const scheduleSalesAgentPaddleCancellation = async (): Promise<{
  cancellationEffectiveAt?: string;
}> => {
  const res = await axiosInstance.post(
    '/billing/paddle/sales-agent/subscription/cancel'
  );
  return unwrapAxiosData<{ cancellationEffectiveAt?: string }>(res);
};

export const revokeSalesAgentPaddleCancellation = async (): Promise<void> => {
  await axiosInstance.post(
    '/billing/paddle/sales-agent/subscription/cancel/revoke'
  );
};

export type DomesticSalesAgentSubscription = {
  provider: 'toss';
  product: 'sales_agent';
  status: string;
  price: number;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  aiMessageAllowance?: number;
};

export const getDomesticSalesAgentSubscription =
  async (): Promise<DomesticSalesAgentSubscription | null> => {
    const res = await axiosInstance.get(
      '/billing/domestic/sales-agent/subscription'
    );
    return unwrapAxiosData<DomesticSalesAgentSubscription | null>(res);
  };

export const startDomesticSalesAgentSubscription = async (
  requestId: string
): Promise<DomesticSalesAgentSubscription> => {
  const res = await axiosInstance.post(
    '/billing/domestic/sales-agent/subscription',
    { requestId }
  );
  return unwrapAxiosData<DomesticSalesAgentSubscription>(res);
};

export const scheduleDomesticSalesAgentCancellation =
  async (): Promise<DomesticSalesAgentSubscription> => {
    const res = await axiosInstance.post(
      '/billing/domestic/sales-agent/subscription/cancel'
    );
    return unwrapAxiosData<DomesticSalesAgentSubscription>(res);
  };

export const revokeDomesticSalesAgentCancellation =
  async (): Promise<DomesticSalesAgentSubscription> => {
    const res = await axiosInstance.post(
      '/billing/domestic/sales-agent/subscription/cancel/revoke'
    );
    return unwrapAxiosData<DomesticSalesAgentSubscription>(res);
  };

export type PaddleSubscriptionStatus = {
  status: string;
  tier?: string;
  currentPeriodEnd?: string;
  pendingTier?: string;
  pendingTierEffectiveAt?: string;
  pendingUsageCheckoutUrl?: string;
};

export const getPaddleSubscription =
  async (): Promise<PaddleSubscriptionStatus | null> => {
    const res = await axiosInstance.get('/billing/paddle/subscription');
    return unwrapAxiosData<PaddleSubscriptionStatus | null>(res);
  };

export type PaddleSubscriptionUpgradePreview = {
  immediateCharge: string;
  nextRecurringCharge: string;
  currencyCode: string;
};

export type PaddleRefundConsent = {
  immediateServiceConsent: true;
  refundPolicyVersion: string;
};

export const previewPaddleSubscriptionUpgrade = async (
  plan: PaddleCheckoutPlanKey
): Promise<PaddleSubscriptionUpgradePreview> => {
  const res = await axiosInstance.post(
    '/billing/paddle/subscription/upgrade/preview',
    { plan }
  );
  return unwrapAxiosData<PaddleSubscriptionUpgradePreview>(res);
};

export const upgradePaddleSubscription = async (
  plan: PaddleCheckoutPlanKey,
  consent?: PaddleRefundConsent
): Promise<void> => {
  await axiosInstance.post('/billing/paddle/subscription/upgrade', {
    plan,
    ...consent,
  });
};

export const schedulePaddleSubscriptionDowngrade = async (
  plan: PaddleCheckoutPlanKey
): Promise<{ effectiveAt: string }> => {
  const res = await axiosInstance.post(
    '/billing/paddle/subscription/downgrade',
    { plan }
  );
  return unwrapAxiosData<{ effectiveAt: string }>(res);
};

export const revokePaddleSubscriptionDowngrade = async (): Promise<void> => {
  await axiosInstance.post('/billing/paddle/subscription/downgrade/revoke');
};

export type PaddleUsageCharge = {
  usageType:
    | 'ocr'
    | 'item_scheduling'
    | 'message_overage'
    | 'storage_overage'
    | 'additional_agent'
    | 'specialized_agent'
    | 'speech_stt_overage'
    | 'speech_tts_overage';
  quantity: number;
  billedQuantity?: number;
  status: 'pending' | 'processing' | 'charged' | 'failed';
  periodEnd: string;
  agentName?: string;
};

export const getPaddleUsageCharges = async (): Promise<PaddleUsageCharge[]> => {
  const res = await axiosInstance.get('/billing/paddle/usage-charges');
  return unwrapAxiosData<PaddleUsageCharge[]>(res);
};
